import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, IconButton, CircularProgress, 
  TextField, Button, Dialog, DialogActions, 
  DialogContent, DialogTitle, LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { styled } from "@mui/material/styles";
import ReactMarkdown from 'react-markdown';
import Lottie from 'lottie-react';
import loaderAnimation from '../../assets/loader_anim.json';

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#5a5a5a',
  display: 'inline-block',
  marginRight: theme.spacing(2),
}));

const StyledMarkdown = styled(ReactMarkdown)`
  & p {
    margin: 0.5em 0;
    font-family: ${props => props.theme.typography.fontFamily};
    font-size: 1rem;
    line-height: 1.5;
  }
  & strong {
    font-weight: 600;
  }
  & ul {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }
  & li {
    margin: 0.25em 0;
  }
`;

const ANALYSIS_MESSAGES = [
  "Analyzing gait cycle patterns...",
  "Examining joint angle variations...",
  "Comparing with baseline metrics...",
  "Evaluating step symmetry...",
  "Assessing movement quality...",
  "Identifying key insights...",
  "Generating recommendations...",
  "Processing biomechanical data...",
  "Calculating deviation patterns...",
  "Finalizing comprehensive analysis..."
];

const LoadingAnimation = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Rotate messages every 6 seconds
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ANALYSIS_MESSAGES.length);
    }, 6000);

    // Smooth progress animation over ~2 minutes
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) return prev; // Stop at 95% until actual completion
        return prev + 0.5;
      });
    }, 600);

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      alignItems="center" 
      justifyContent="center"
      sx={{ py: 4 }}
    >
      <Box sx={{ width: 100, height: 100, mb: 2 }}>
        <Lottie 
          animationData={loaderAnimation}
          loop={true}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
      
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2, 
          color: '#5a5a5a',
          fontWeight: 500,
          textAlign: 'center',
          minHeight: '32px',
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        {ANALYSIS_MESSAGES[messageIndex]}
      </Typography>
      
      <Box sx={{ width: '80%', mb: 1 }}>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: '#63C7CF',
            }
          }}
        />
      </Box>
      <Typography variant="caption" color="textSecondary">
        This may take 1-2 minutes...
      </Typography>
    </Box>
  );
};

const AIAnalysisBox = ({ isAIAnalysis, setIsAIAnalysis, aiAnalysisResult, loading, error }) => {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isPositiveFeedback, setIsPositiveFeedback] = useState(true);

  if (!isAIAnalysis) return null;

  const handleThumbUp = () => {
    setIsPositiveFeedback(true);
    setFeedbackDialogOpen(true);
  };

  const handleThumbDown = () => {
    setIsPositiveFeedback(false);
    setFeedbackDialogOpen(true);
  };

  const handleFeedbackSubmit = () => {
    console.log(`${isPositiveFeedback ? 'Positive' : 'Negative'} feedback received:`, feedbackText);
    setFeedbackDialogOpen(false);
    setFeedbackText('');
  };

  return (
    <Box sx={{ p: 2, border: '1px solid #E0E0E0', borderRadius: '4px', backgroundColor: '#FAFAFA', marginY: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <StyledTypography variant="h6" color="secondary">AI Analysis (Beta)</StyledTypography>
        <Box>
          <IconButton 
            onClick={handleThumbUp} 
            sx={{ color: '#63C7CF', '&:hover': { backgroundColor: 'rgba(99, 199, 207, 0.08)' } }}
          >
            <ThumbUpIcon />
          </IconButton>
          <IconButton 
            onClick={handleThumbDown} 
            sx={{ color: '#63C7CF', '&:hover': { backgroundColor: 'rgba(99, 199, 207, 0.08)' } }}
          >
            <ThumbDownIcon />
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={() => setIsAIAnalysis(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box pl={2} pr={2} pb={2}>
        {loading && <LoadingAnimation />}
        {error && <Typography color="error">{error}</Typography>}
        {aiAnalysisResult && (
          <StyledMarkdown>{aiAnalysisResult.analysis}</StyledMarkdown>
        )}
      </Box>

      <Dialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)}>
        <DialogTitle>{isPositiveFeedback ? "Provide Positive Feedback" : "Provide Feedback for Improvement"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="feedback"
            label={isPositiveFeedback ? "What did you like?" : "What could be improved?"}
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleFeedbackSubmit} 
            variant="contained" 
            sx={{ 
              backgroundColor: '#63C7CF', 
              '&:hover': { backgroundColor: '#50B0B8' } 
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIAnalysisBox;